import { Skeleton } from '@/components/ui/skeleton'
import DatePicker, { DatePickerMode } from '@/components/DatePicker'
import { useState, useEffect } from 'react'
import { DateRange } from 'react-day-picker'

interface MetricCardProps {
  title: string
  value: number
  change: number
  changeText: string
  period: string | Date | DateRange
  onPeriodChange: (period: string | Date | DateRange) => void
  loading?: boolean
  className?: string
  onCardClick?: () => void
}

export function MetricCard({
  title,
  value,
  change,
  changeText,
  period,
  onPeriodChange,
  loading = false,
  onCardClick,
  className
}: MetricCardProps) {
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>({
    from: new Date(),
    to: new Date()
  })

  useEffect(() => {
    if (period instanceof Date) {
      setSelectedRange({ from: period, to: period })
    } else if (period && typeof period === 'object' && 'from' in period) {
      setSelectedRange(period as DateRange)
    } else {
      const today = new Date()
      setSelectedRange({ from: today, to: today })
    }
  }, [period])

  const handleDateChange = (range: Date | DateRange | undefined) => {
    if (range) {
      if ('from' in range) {
        setSelectedRange(range)
        onPeriodChange(range)
      } else {
        const dateRange = { from: range, to: range }
        setSelectedRange(dateRange)
        onPeriodChange(dateRange)
      }
    }
  }

  const isPositive = change >= 0
  const changeColor = isPositive ? 'text-green-600' : 'text-rose-600'
  const changePrefix = isPositive ? '+' : ''
  const formattedChange = change.toFixed(3)

  return (
    <div className={`bg-white rounded-lg border border-[#E5E5E5] p-4 flex flex-col ${className}`}>
      <div className="flex flex-col gap-2 mb-3">
        <span className="font-medium text-xs">{title}</span>
        <DatePicker
          value={selectedRange}
          onChange={handleDateChange}
          className="h-7 text-xs"
          mode={DatePickerMode.Range}
        />
      </div>
      {loading ? (
        <div className="flex gap-2 justify-between items-end flex-1">
          <div className="flex flex-col gap-2 flex-1">
            <Skeleton className="h-8 w-24" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="w-10 h-8 rounded-lg" />
        </div>
      ) : (
        <div className="flex gap-2 justify-between items-end flex-1 min-w-0">
          <div className="flex flex-col min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-sans font-bold text-2xl truncate">
                {value}
              </span>
            </div>
            <div className="flex gap-1 font-sans font-normal text-xs flex-wrap">
              <span className={changeColor}>
                {changePrefix}{formattedChange}%
              </span>
              {changeText}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
